/**
 * Clinic permission keys this frontend renders against (verified catalogue,
 * consumer-side constants — the authoritative registry is Auth's).
 * Rendering rule: absent, not disabled. fullClinicAdmin satisfies every key.
 */

export const PERM = {
  settingsRead: 'clinic.settings.read',
  settingsManage: 'clinic.settings.manage',
  personnelRead: 'clinic.personnel.read',
  personnelManage: 'clinic.personnel.manage',
  catalogRead: 'clinic.catalog.read',
  catalogManage: 'clinic.catalog.manage',
  roomsRead: 'clinic.rooms.read',
  roomsManage: 'clinic.rooms.manage',
  capabilitiesRead: 'clinic.capabilities.read',
  capabilitiesManage: 'clinic.capabilities.manage',
  availabilityRead: 'clinic.availability.read',
  availabilityManage: 'clinic.availability.manage',
  patientsRead: 'clinic.patients.read',
  patientsUpdate: 'clinic.patients.update',
  assessmentsRead: 'clinic.assessments.read',
  assessmentsWrite: 'clinic.assessments.write',
  assessmentsComplete: 'clinic.assessments.complete',
  packagesRead: 'clinic.packages.read',
  packagesManage: 'clinic.packages.manage',
  bookingsRead: 'clinic.bookings.read',
  bookingsSchedule: 'clinic.bookings.schedule',
  bookingsReschedule: 'clinic.bookings.reschedule',
  bookingsCancel: 'clinic.bookings.cancel',
  sessionsRead: 'clinic.sessions.read',
  sessionsDeliver: 'clinic.sessions.deliver',
  sessionsCorrect: 'clinic.sessions.correct',
  paymentRecord: 'clinic.sessions.payment.record',
  /** Clinical instruments (ZOY-594): a dedicated family — read / administer a draft / finalize + supersede. */
  instrumentsRead: 'clinic.instruments.read',
  instrumentsAdminister: 'clinic.instruments.administer',
  instrumentsFinalize: 'clinic.instruments.finalize',
  /** Auth-side clinic administration keys (roles/membership live in Auth). */
  membersManage: 'clinic.members.manage',
  rolesManage: 'clinic.roles.manage',
  /** Family connections (ZOY-665): read shows the surfaces, manage enables the actions. */
  familiesRead: 'clinic.families.read',
  familiesManage: 'clinic.families.manage',
  /**
   * Care Plan (ZOY-667): read covers current AND historical reads (the
   * former `clinic.plan.history.read` is retired in Auth); author is home-task
   * authoring only.
   */
  planRead: 'clinic.plan.read',
  planAuthor: 'clinic.plan.author',
  /**
   * Management reports (ZOY-701/702): the ONE key of the Dashboard. It grants
   * the clinic-wide aggregates (financial ones included) and nothing beneath
   * them — every drill-down stays behind its own key.
   */
  reportsRead: 'clinic.reports.read',
  /**
   * Family session feedback (ZOY-718): what families said about delivered
   * sessions, per therapist, WITH the patient and family member named. Its own
   * key, implied by no other — a therapist sees it only if the clinic grants it.
   */
  feedbackRead: 'clinic.feedback.read',
} as const;

export type PermissionKey = (typeof PERM)[keyof typeof PERM] | (string & {});

export interface AuthzView {
  fullClinicAdmin: boolean;
  permissions: ReadonlySet<string>;
}

export function can(view: AuthzView, key: PermissionKey): boolean {
  return view.fullClinicAdmin || view.permissions.has(key);
}
